import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText } from "@mui/material";
import React, { createContext, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router";
import { disablePersonFromCompany, enablePersonFromCompany } from "../../api/api";
import { createMillInvite, millInviteError, revokeMillInvite } from "../../api/millInvite";
import { GetOrgDetail } from '../../api/orgs';
import { Loader } from '../../components/Loader';
import MapBox from "../../components/MapBox";
import { defaultLat, defaultLng, showErrorMessage, showSuccessMessage } from "../../helper/helper";
import { personId, transactorId } from "../../helper/TokenHelper";
import { Employee, OrganisationModel } from '../../models/organisation/OrganisationModelV2';
import { Person } from "../../models/person/Person";
import { colorLight, colorPrimaryDark } from "../../rsc/colors/colors";
import { ReactComponent as EditIcon } from "../../rsc/ic_edit.svg";
import { ReactComponent as Expand } from "../../rsc/ic_expand.svg";
import { ReactComponent as LocationIcon } from "../../rsc/ic_location.svg";
import { ReactComponent as PhoneIcon } from "../../rsc/ic_phone.svg";
import { ReactComponent as Reduce } from "../../rsc/ic_reduce.svg";
import { ReactComponent as TaxIcon } from "../../rsc/ic_tax.svg";
import { CompanyQBP } from './CompanyQBP';
import { EditMillPosition } from './EditMillPosition';
import { EmployeeSettingsButton } from './EmployeeSettingsButton';
import EnableEmployeeToggle from './EnableEmployeeToggle';
import UserInvitationDialog from './UserInvitationDialog';
import { PaymentInformation } from './PaymentInformation';
import { useAppContext } from '../../contexts/AppContext';


type CompanyDetailProps = {
    client: ApolloClient<NormalizedCacheObject>,
}

export type CompanyContextData = {
    reload: (() => void) | undefined
}

export const CompanyContext = createContext<CompanyContextData>({ reload: undefined })

function CompanyDetail(props: CompanyDetailProps) {

    const { millSelected } = useAppContext()

    const mapExpandInfo = {
        isExpand: true,
        mapHeight: "500px"
    }

    const mapReducedInfo = {
        isExpand: false,
        mapHeight: "300px"
    }

    const { t } = useTranslation();

    const navigate = useNavigate()

    const [organisation, setOrganisation] = useState<OrganisationModel>(null)
    const [employeeToRemove, setEmployeeToRemove] = useState<Employee>(null)
    const [employeeToInvite, setEmployeeToInvite] = useState<Employee>(null)
    const [coords, setCoords] = useState([defaultLng, defaultLat])
    const [mapSizeInfo, setMapSizeInfo] = useState(mapReducedInfo)
    const [isMapLoaded, setIsMapLoaded] = useState(false)
    const [notifications, setNotifications] = useState<Map<number, boolean>>(new Map())

    const mapRef = useRef(null)

    // millSelected is undefined until the user picks a mill, and `variables` is
    // evaluated even when the query is skipped.
    const millId = millSelected?.id ? parseInt(millSelected.id) : undefined

    const { data, loading, error, refetch } = useQuery(GetOrgDetail, {
        skip: !millId,
        variables: {
            id: millId,
            input: {
                organisation_id: millId
            }
        },
        context: {
            headers: {
                "role": "usermill"
            }
        }
    })

    useEffect(() => {
        if (!data) return
        const org = OrganisationModel.build(data)
        setCoords(org.organisation.position.coordinates)
        setOrganisation(org)
    }, [data])

    useEffect(() => {
        if (!millId) return
        refetch()
    }, [millId])

    const EmployeeHeader = () => {
        return <tr>
            <th style={{ width: "25%" }}>{t('name')}</th>
            <th style={{ width: "25%" }}>{t('location')}</th>
            <th style={{ width: "25%" }}>{t('email')}</th>
            <th style={{ width: "25%" }}></th>
        </tr>
    }

    const EmployeeAvatar = (firstname: string, lastname: string, uri?: string) => {
        if (uri != null) {
            const prefix = "https://storage.googleapis.com/"
            const modUri = uri.includes("gs:") ? uri.replace("gs://", prefix) : prefix + uri
            return <div style={{ display: "inline-block", verticalAlign: "middle" }}><img alt="avatar" style={{ width: "40px", borderRadius: "50%", height: "40px" }} src={modUri}>
            </img></div>
        } else {
            return <div className='primaryDarkBgColor' style={{
                display: "inline-block", verticalAlign: "middle",
                textAlign: "center", width: "40px", borderRadius: "50%", height: "40px"
            }}>
                <div style={{ transform: "translateY(-50%)", color: "white", position: "relative", top: "50%", }}>{firstname.charAt(0)}{lastname.charAt(0)}</div></div>
        }
    }


    // The invite endpoint upserts on (email, orgId), so re-inviting is just a
    // repeat POST — it refreshes the oob and resends the email.
    async function reinviteUser(person: Person) {
        try {
            await createMillInvite({
                email: person.user.email,
                inviterOrganisationId: parseInt(millSelected.id),
                firstname: person.firstname,
                lastname: person.lastname
            })
            showSuccessMessage(t('invitation_sent'))
            setEmployeeToInvite(null)
            refetch()
        } catch (e) {
            showErrorMessage(millInviteError(e))
        }
    }

    const removePerson = async (employee: Employee, orgId: number) => {

        if (employee.personId() === -1) {
            try {
                await revokeMillInvite(employee.person.user.email, orgId)
                showSuccessMessage(t('invitation_revoked'))
                setEmployeeToRemove(null)
                refetch()
            } catch (e) {
                showErrorMessage(millInviteError(e))
            }
        } else {
            const query = employee.isEnable() ? disablePersonFromCompany : enablePersonFromCompany
            const variables = {
                input: {
                    person_id: employee.person.id,
                    organisation_id: orgId,
                }
            }
            props.client.mutate({
                mutation: query,
                variables: variables

            }).then(result => {
                if (employee.personId() === transactorId()) {
                    if (organisation.isMill()) {
                        navigate('/explorer/companies')
                    } else {
                        navigate('/explorer/admin')
                    }
                } else {
                    setEmployeeToRemove(null)
                    refetch()
                }
            }).catch(e => showErrorMessage(e))
        }
    }

    const isMe = (employee: Employee): boolean => {
        return employee.personId() == personId()
    }

    const EmployeeRow = (employee: Employee) => {
        var person: Person = employee.person
        if (person.id === -1) {
            const firstname = person.firstname ? person.firstname : person.user.email[0]
            const lastname = person.lastname ? person.lastname : person.firstname ? "" : person.user.email[1]
            return (
                <tr key={person.user.email} className='yellowLightBgColor'>
                    <td>{EmployeeAvatar(firstname, lastname, person.user?.avatar_url)}<span style={{ marginLeft: "10px" }}>{person.firstname ? `${firstname} ${lastname}` : person.user.email}</span></td>
                    <td style={{ color: "red" }}>{t('invited_user')}</td>
                    <td style={{ color: "red" }}>{person.user.email}</td>
                    <td style={{ textAlign: "center" }}>
                        {
                            organisation?.canWrite(personId()) ?
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <div style={{ flex: "1", display: "flex", justifyContent: "end" }}>
                                        <button className="button-no-background primaryColor" onClick={() => { setEmployeeToInvite(employee) }}>{t('invite_user')}</button>
                                    </div>
                                    <div style={{ flex: "1", display: "flex", justifyContent: "start", marginLeft: "10px" }}>
                                        <button className="button-no-background" onClick={() => { displayRemovePersonDialog(employee) }} style={{ color: "red" }}>{t('remove')}</button>
                                    </div>
                                </div>
                                : <></>
                        }
                    </td>
                </tr>
            )
        } else if (person) {
            return <tr key={employee.personId()}>
                <td>{EmployeeAvatar(person.firstname, person.lastname, person?.user?.avatar_url)}<span style={{ marginLeft: "10px" }}>{person.firstname} {person.lastname}</span></td>
                <td>{person.location?.village}</td>
                <td>{person?.email}</td>
                <td style={{ textAlign: "center" }}>
                    {
                        organisation?.canWrite(personId()) && !isMe(employee) &&
                        <div className='pempem-flex-col-center'>
                            <EnableEmployeeToggle employee={employee} orgId={organisation.organisation.id} dbRefetch={refetch} />
                            <EmployeeSettingsButton employee={employee} dbRefetch={refetch} orgId={organisation.organisation.id} />
                        </div>
                    }
                </td>
            </tr>
        }
    }

    const displayRemovePersonDialog = (employee: Employee) => {
        setEmployeeToRemove(employee)
    }

    const handleNotificationToggle = (employeeId: number) => {
        const newNotifications = new Map(notifications)
        newNotifications.set(employeeId, !notifications.get(employeeId))
        setNotifications(newNotifications)
        // Here you could also make an API call to save the notification preference
        // handleMenuClose()
    }

    const companyName = () => {
        if (organisation?.organisation) {
            return organisation.organisation.name
        }
        return "Company Detail"
    }

    function changeMapSize() {
        if (mapSizeInfo.isExpand) {
            setMapSizeInfo(mapReducedInfo)
        } else {
            setMapSizeInfo(mapExpandInfo)
        }
        setTimeout(() => {
            mapRef.current.resize()
        }, 100)
    }

    function showCompanyInformations() {
        if (organisation?.isMill()) {
            return (
                <div style={{
                    background: 'var(--color-bg)',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    margin: '0 28px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                }}>
                    {/* Address row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '8px',
                            background: 'var(--color-primary-pale, rgba(13,138,159,0.1))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <LocationIcon style={{ width: '18px', height: '18px' }} />
                        </div>
                        <span style={{ fontSize: '14px', opacity: 0.85 }}>
                            {organisation.formatAddress() ?? '-'}
                        </span>
                    </div>

                    <div style={{ borderTop: '1px solid var(--color-light-grey, #e5e7eb)' }} />

                    {/* Phone & Tax row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '8px',
                                background: 'var(--color-primary-pale, rgba(13,138,159,0.1))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <PhoneIcon style={{ width: '18px', height: '18px' }} fill={colorPrimaryDark()} />
                            </div>
                            <span style={{ fontSize: '14px', opacity: 0.85 }}>
                                {organisation.economics?.legal_details?.phone_number ?? '-'}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '8px',
                                background: 'var(--color-primary-pale, rgba(13,138,159,0.1))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <TaxIcon style={{ width: '18px', height: '18px' }} />
                            </div>
                            <span style={{ fontSize: '14px', opacity: 0.85 }}>
                                {organisation.economics?.legal_details?.vat_number ?? '-'}
                            </span>
                        </div>
                    </div>
                </div>
            )
        } else {
            return <></>
        }
    }

    return (
        <CompanyContext.Provider value={{ reload: refetch }}>
            <div style={{ width: "100%", paddingBottom: "20px" }}>
                <div style={{
                    padding: '20px 28px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                }}>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>{companyName()}</h1>
                    {organisation?.isMill() &&
                        <Button
                            onClick={() => navigate("/explorer/company_profil/" + organisation.organisation.id)}
                            variant="outlined"
                            startIcon={<EditIcon fill={colorPrimaryDark()}  style={{ width: '16px', height: '16px' }} />}
                            style={{
                                borderColor: colorPrimaryDark(),
                                color: colorPrimaryDark(),
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                padding: '6px 18px',
                            }}
                        >
                            {t('edit')}
                        </Button>
                    }
                </div>
                {showCompanyInformations()}
                <div className='page-padding pempem-flex-col gap10'>
                    {organisation?.isMill() &&
                        <div style={{ position: "relative", height: mapSizeInfo.mapHeight }}>
                            <MapBox
                                width="100%"
                                lng={coords[0]}
                                lat={coords[1]}
                                stateToReload={coords}
                                height="100%"
                                onLoaded={(map) => { mapRef.current = map; setIsMapLoaded(true) }}
                            />
                            {
                                isMapLoaded &&
                                <div>
                                    <EditMillPosition map={mapRef.current} org={organisation.organisation} />
                                    <div onClick={changeMapSize} style={{
                                        cursor: "pointer",
                                        width: "40px",
                                        height: "40px",
                                        position: "absolute",
                                        left: "40px",
                                        borderRadius: "5px",
                                        top: "30px",
                                        background: colorPrimaryDark(),
                                        display: "flex",
                                        padding: "10px"
                                    }}>
                                        {
                                            mapSizeInfo.isExpand ? <Reduce style={{ width: "100%", height: "100%" }} />
                                                : <Expand style={{ width: "100%", height: "100%" }} />
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }
                    {/* <CompanyGroup groups={groups ?? []} style={{ padding: "28px" }} /> */}
                    <div className='pempem-flex-row gap10' style={{
                        marginTop: 10,
                        flexWrap: 'wrap'
                    }}>
                        <div style={{
                            flex: '1 0 0',
                            minWidth:'300px'
                        }}>
                            <CompanyQBP org={organisation} />
                        </div>
                        <div style={{
                            flex: '1 0 0',
                            minWidth:'300px'
                        }}>
                            <PaymentInformation org={organisation} onRefetch={refetch} />
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "end", justifyContent: "space-between" }}>
                        <span className="greyColor bold" style={{ fontSize: "20px" }}>{t('employee')}</span>
                        <UserInvitationDialog organisation={organisation?.organisation} onSuccess={refetch} />
                    </div>
                    {
                        organisation?.employees?.length > 0 ?
                            <table className="pem-table">
                                <thead>
                                    {EmployeeHeader()}
                                </thead>
                                <tbody>
                                    {(() => {
                                        if (organisation != null) {
                                            return organisation?.employees?.map((employee) => EmployeeRow(employee))
                                        } else {
                                            return <tr><td></td></tr>
                                        }
                                    })()
                                    }
                                </tbody>
                            </table>
                            : <></>
                    }
                    <Dialog
                        open={employeeToRemove !== null}
                        onClose={() => setEmployeeToRemove(null)}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                {employeeToRemove?.isEnable() ?
                                    t('disable_person_company',
                                        {
                                            name: employeeToRemove?.person?.getFullName()
                                                ? employeeToRemove?.person?.getFullName()
                                                : employeeToRemove?.person?.user?.email
                                        }) :
                                    t('enable_person_company',
                                        {
                                            name: employeeToRemove?.person?.getFullName()
                                                ? employeeToRemove?.person?.getFullName()
                                                : employeeToRemove?.person?.user?.email
                                        })
                                }
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => removePerson(employeeToRemove, millSelected.id)}>{t('yes')}</Button>
                            <Button onClick={() => setEmployeeToRemove(null)} autoFocus>
                                {t('no')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={employeeToInvite !== null}
                        onClose={() => setEmployeeToInvite(null)}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description">
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                {t('reinvite_message', { name: employeeToInvite?.person?.getFullName() })}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => reinviteUser(employeeToInvite?.person)}>{t('yes')}</Button>
                            <Button onClick={() => setEmployeeToInvite(null)} autoFocus>
                                {t('no')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Loader isLoading={loading} />
                </div>
            </div >
        </CompanyContext.Provider>
    )
}

export default CompanyDetail